import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import TextButton from "@shared/v2/TextButton";
import CardAppointment from "./CardAppointment";
import TaskModal from "../../../Tasks/components/TaskModal";
import Details from "./Details";
import ViewAllAppointmentsModal from "../modals/ViewAllAppointmentsModal";
import CollapsibleElement from "../CollapsibleElement";
import * as actions from "../../actions/personDetailActionCreators";
import Loading from "../../../Goals/components/Loader";
import { useCurrentUser } from "../../../reducers/layoutReducer/selectors";
import DeleteModal from "../../../Tasks/components/DeleteModal";
import AppointmentCompleteModal from "../modals/AppointmentCompleteModal";
import { updateTaskById } from "../../../Tasks/components/services";

const CardAppointments = () => {
  const dispatch = useDispatch();
  const [state, _setState] = useState({
    viewAlltasks: false,
    viewDetails: false,
    selectedTask: undefined,
    viewAppointmentModal: false,
    editAppointment: false,
    deleteAppointmentModal: false,
    completeAppointmentModal: false,
    addAppointmentModalOpen: false,
  });
  const setState = (newState) => _setState((prevState) => ({ ...prevState, ...newState }));

  const currentUser = useCurrentUser();
  const person = useSelector(({ personDetail }) => personDetail.person);
  const incompleteAppointments = useSelector(
    ({ personDetail }) => personDetail.appointments.filter((item) => item.state !== "archived") || [],
  );
  const viewAllAppointments = useSelector(
    ({ personDetail }) => personDetail.allAppointments.filter((item) => item.state !== "archived") || [],
  );
  const allAppointmentCount = useSelector(({ personDetail }) => personDetail.allAppointmentCount);

  const getAppointments = (personUuid, limit = 3) => dispatch(actions.getAppointments(personUuid, limit));
  useEffect(() => {
    if (person.data?.attributes.uuid) getAppointments(person.data.attributes.uuid, 3);
  }, [person.data?.attributes.uuid]);

  const openAppointmentModal = () => {
    if (person.data?.attributes.uuid) getAppointments(person.data.attributes.uuid, 100);
    setState({ viewAppointmentModal: true });
  };
  const closeAppointmentModal = () => {
    if (person.data?.attributes.uuid) getAppointments(person.data.attributes.uuid, 3);
    setState({ viewAppointmentModal: false });
  };
  const showTaskDetails = (task) => setState({ viewDetails: true, selectedTask: task });
  const editTask = (task) => setState({ viewDetails: false, selectedTask: task, editAppointment: true });
  const deleteTask = (task) =>
    setState({ viewDetails: false, selectedTask: task, deleteAppointmentModal: true });
  const completeTask = (task) =>
    setState({
      viewDetails: false,
      selectedTask: {
        ...task,
        state: "completed",
        completed: "true",
        completed_by_user_id: currentUser?.uuid,
      },
      completeAppointmentModal: true,
    });
  const closeTaskDetails = () =>
    setState({
      viewDetails: false,
      selectedTask: undefined,
      editAppointment: false,
      deleteAppointmentModal: false,
      completeAppointmentModal: false,
    });

  const handleDeleteTask = () => {
    closeTaskDetails();
    const limit = state.viewAppointmentModal ? 100 : 3;
    dispatch(actions.deleteTask(state.selectedTask.id, person.data.attributes.uuid, limit));
  };

  const updateQuickViewTask = (taskToUpdate) => dispatch(actions.updateCardTasks(taskToUpdate));

  const onAppointmentCompleted = async () => {
    await updateTaskById({
      id: state.selectedTask.id,
      formData: {
        ...state.selectedTask,
      },
    });
    updateQuickViewTask(state.selectedTask);
    const limit = state.viewAppointmentModal ? 100 : 3;
    if (person.data?.attributes.uuid) getAppointments(person.data.attributes.uuid, limit);

    closeTaskDetails();
  };
  const closeTaskModal = () => setState({ addAppointmentModalOpen: false });

  const openAddAppointmentModal = () => setState({ addAppointmentModalOpen: true });

  const updateTask = (key, value) => {
    setState({ selectedTask: { ...state.selectedTask, [key]: value } });
  };

  const taskable = {
    task_name: `${person.data?.attributes.first_name} ${person.data?.attributes.last_name}`.trim(),
    uuid: person.data?.attributes.uuid,
    taskable_type: "Person",
  };

  return (
    <CollapsibleElement
      id="pdp-appointments"
      title="Appointments"
      buttonText="+ ADD"
      buttonClickFunction={openAddAppointmentModal}
    >
      {!person.data && <Loading />}
      <div id="person-tasks-card" className="tw-relative">
        {incompleteAppointments.length === 0 && (
          <div className="tw-text-12d">
            {allAppointmentCount === 0
              ? "Create an appointment for this contact and assign it to yourself or a teammate."
              : "No upcoming tasks."}
          </div>
        )}

        <div className="tw-flex tw-flex-col tw-gap-[16px]">
          {incompleteAppointments.map((task) => (
            <CardAppointment
              key={task.id}
              task={task}
              showTaskDetails={showTaskDetails}
              onEditTask={editTask}
              onDeleteTask={deleteTask}
              completeTask={completeTask}
            />
          ))}
          {allAppointmentCount > 2 && (
            <TextButton className="tw-self-start" onClick={openAppointmentModal}>
              View All
            </TextButton>
          )}
        </div>

        <ViewAllAppointmentsModal
          viewAllAppointments={viewAllAppointments}
          onClose={closeAppointmentModal}
          editTask={editTask}
          showTaskDetails={showTaskDetails}
          onDeleteTask={deleteTask}
          completeTask={completeTask}
          showViewAllModal={state.viewAppointmentModal}
        />

        {state.viewDetails && (
          <Details
            type="Appointment"
            task={state.selectedTask}
            closeTaskDetails={closeTaskDetails}
            editTask={editTask}
            openComplete={completeTask}
            onDelete={deleteTask}
          />
        )}
        {state.editAppointment && (
          <TaskModal
            person={person}
            onClose={closeTaskDetails}
            user={currentUser}
            taskable={taskable}
            task={state.selectedTask}
            updateTask={updateQuickViewTask}
            isCreatingAppointment
          />
        )}
        <DeleteModal
          closeDeleteModal={closeTaskDetails}
          showDeleteModal={state.deleteAppointmentModal}
          submitDisabled={state.submitDisabled}
          deleteTasksHandler={handleDeleteTask}
          type="Appointment"
        />

        <AppointmentCompleteModal
          closeModal={closeTaskDetails}
          modalOpen={state.completeAppointmentModal}
          handleSubmit={onAppointmentCompleted}
          task={state.selectedTask}
          updateTask={updateTask}
        />

        {state.addAppointmentModalOpen && (
          <TaskModal
            person={person}
            user={currentUser}
            taskable={taskable}
            task={state.selectedTask}
            updateTask={updateQuickViewTask}
            getCardTasks={getAppointments}
            isCreatingAppointment
            onClose={closeTaskModal}
            contactEmail={person.data?.attributes.main_email}
          />
        )}
      </div>
    </CollapsibleElement>
  );
};

export default CardAppointments;
